import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useResponsive } from '../hooks/useResponsive'
import MobileLayout from './mobile/MobileLayout'

interface LayoutProps {
  children: React.ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  // Persistent sidebar state - default to open on desktop
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarOpen')
    return saved !== null ? saved === 'true' : window.innerWidth >= 1024
  })
  const { isMobile } = useResponsive()

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarOpen', String(sidebarOpen))
  }, [sidebarOpen])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const navigation = [
    { name: 'Odoo Inventory', href: '/inventory', icon: '📋', color: 'from-green-600 to-green-700' },
    { name: 'Virtual Warehouse', href: '/warehouse', icon: '🏭', color: 'from-emerald-600 to-teal-700' },
    { name: 'Sales Dashboard', href: '/sales', icon: '💰', color: 'from-green-600 to-emerald-700' },
    { name: 'Website Manager', href: '/website', icon: '🌐', color: 'from-blue-600 to-cyan-700' },
    { name: 'Premium Catalog', href: '/premium-catalog', icon: '🀄', color: 'from-red-600 to-orange-700' },
    { name: 'PokeData Catalog', href: '/pokedata', icon: '🎴', color: 'from-pink-600 to-pink-700' },
    { name: 'TCG Oracle', href: '/tcg-oracle', icon: '🧠', color: 'from-purple-600 to-purple-700' },
    { name: 'Box Search', href: '/tcg-box-search', icon: '🔍', color: 'from-emerald-600 to-emerald-700' },
    { name: 'Database Explorer', href: '/db', icon: '🗄️', color: 'from-blue-600 to-blue-700' },
    { name: 'Server Explorer', href: '/server', icon: '⚡', color: 'from-purple-600 to-purple-700' },
  ]

  // Render mobile layout for mobile devices
  if (isMobile) {
    const sidebarContent = (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--color-accent-dark)' }}>
          <h2 className="text-lg font-semibold text-white">Menu</h2>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 text-gray-400 hover:text-white rounded-lg transition-colors"
          >
            ✕
          </button>
        </div>
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`
                  flex items-center px-4 py-3 rounded-lg transition-all duration-200 relative group
                  ${isActive 
                    ? 'text-white' 
                    : 'text-gray-300 hover:text-white hover:bg-slate-700'
                  }
                `}
                style={isActive ? { backgroundColor: 'var(--color-interactive)' } : {}}
                onClick={() => setSidebarOpen(false)}
              >
                <span className="relative mr-3 text-lg">{item.icon}</span>
                <span className="relative">{item.name}</span>
              </Link>
            )
          })}
        </nav>
        <div className="px-4 py-4 border-t" style={{ borderColor: 'var(--color-accent-dark)' }}>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
              <span className="text-white text-sm font-bold">
                {user?.username?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                {user?.username || 'Admin'}
              </p>
              <p className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>
                {user?.email || 'admin@example.com'}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="text-gray-400 hover:text-red-400 transition-colors p-1"
              title="Logout"
            >
              <span className="text-lg">🚪</span>
            </button>
          </div>
        </div>
      </div>
    );

    return (
      <MobileLayout 
        sidebarContent={sidebarContent}
        onSidebarToggle={setSidebarOpen}
      >
        {children}
      </MobileLayout>
    );
  }

  // Desktop layout continues below
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-primary-dark)' }}>
      {/* Mobile/Tablet sidebar overlay */}
      {sidebarOpen && !isMobile && window.innerWidth < 1024 && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 backdrop-blur-dark"></div>
        </div>
      )}

      {/* Sidebar - Always toggleable */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
      style={{ backgroundColor: 'var(--color-secondary-dark)' }}>
        <div className="flex flex-col h-full border-r" style={{ borderColor: 'var(--color-accent-dark)' }}>
          {/* Logo */}
          <div className="gradient-header flex items-center justify-between h-16 px-4">
            <h1 className="text-xl font-bold text-white text-shadow">Trading Cards Admin</h1>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="text-white hover:text-gray-200 transition-colors"
              title="Close sidebar"
            >
              ✕
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`group flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 relative overflow-hidden ${
                    isActive
                      ? 'text-white shadow-lg'
                      : 'hover:bg-opacity-50 hover:text-white'
                  }`}
                  style={{
                    backgroundColor: isActive ? 'var(--color-interactive)' : 'transparent',
                    color: isActive ? 'white' : 'var(--color-text-secondary)'
                  }}
                >
                  {isActive && (
                    <div className="absolute inset-0 bg-gradient-to-r opacity-80" 
                         style={{ background: `linear-gradient(135deg, var(--color-interactive), var(--color-interactive-hover))` }}>
                    </div>
                  )}
                  {!isActive && (
                    <div className="absolute inset-0 bg-gradient-to-r opacity-0 group-hover:opacity-20 transition-opacity duration-200"
                         style={{ backgroundColor: 'var(--color-tertiary-dark)' }}>
                    </div>
                  )}
                  <span className="relative mr-3 text-lg">{item.icon}</span>
                  <span className="relative">{item.name}</span>
                  {isActive && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-white rounded-r"></div>
                  )}
                </Link>
              )
            })}
          </nav>

          {/* User menu */}
          <div className="px-4 py-4 border-t" style={{ borderColor: 'var(--color-accent-dark)' }}>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                <span className="text-white text-sm font-bold">
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                  {user?.username || 'Admin'}
                </p>
                <p className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>
                  {user?.email || 'admin@example.com'}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="text-gray-400 hover:text-red-400 transition-colors p-1"
                title="Logout"
              >
                <span className="text-lg">🚪</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-0'}`}>
        {/* Top bar */}
        <div className="sticky top-0 z-40 bg-opacity-80 backdrop-blur-sm border-b"
             style={{ 
               backgroundColor: 'var(--color-secondary-dark)', 
               borderColor: 'var(--color-accent-dark)' 
             }}>
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-gray-400 hover:text-white transition-colors"
              title={sidebarOpen ? "Close sidebar" : "Open sidebar"}
            >
              <span className="text-xl">☰</span>
            </button>
            
            <div className="flex items-center space-x-4">
              <div className="hidden sm:block">
                <span className="text-sm text-gray-400">
                  Welcome back, <span style={{ color: 'var(--color-text-primary)' }}>{user?.username || 'Admin'}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}
